/**
 * applyBulkActiveForPurchase — extraído de ForecastPage.tsx (P7 split).
 *
 * Activa/desactiva `activeForPurchase` para los productos seleccionados:
 *   1) Update optimista (in-memory + revert si falla).
 *   2) PATCH paralelo a /forecasting/product-mapping/:id/active-for-purchase.
 *   3) Reconcilia: revierte fallos, sincroniza valores del servidor.
 *
 * Recibe setters como callbacks para mantener la lógica pura.
 */

import { apiClient } from "@/services/apiClient";
import { ProductData } from "./types";

interface ApplyBulkActiveOptions {
  mode: "on" | "off";
  selectedIds: Set<string>;
  setProducts: (
    updater: (curr: ProductData[]) => ProductData[],
  ) => void;
  setSelectedIds: (
    updater: (prev: Set<string>) => Set<string>,
  ) => void;
  closeActionsMenu: () => void;
}

export async function applyBulkActiveForPurchase(
  opts: ApplyBulkActiveOptions,
): Promise<void> {
  const { mode, selectedIds, setProducts, setSelectedIds, closeActionsMenu } =
    opts;
  if (selectedIds.size === 0) return;
  const ids = Array.from(selectedIds);

  // Keep previous values per id to revert on failure
  const prev = new Map<string, boolean | undefined>();
  setProducts((curr) =>
    curr.map((p) => {
      if (!ids.includes(p.unifiedProductId)) return p;
      prev.set(p.unifiedProductId, p.activeForPurchase);
      if (mode === "on") return { ...p, activeForPurchase: true };
      return { ...p, activeForPurchase: false };
    }),
  );

  // Fire requests in parallel
  const results = await Promise.allSettled(
    ids.map(async (id) => {
      const body = { active: mode === "on" };
      const res = await apiClient.patch(
        `/forecasting/product-mapping/${encodeURIComponent(id)}/active-for-purchase`,
        body,
      );
      const json = res.data as { data?: { activeForPurchase?: boolean } };
      return { id, value: !!json?.data?.activeForPurchase } as const;
    }),
  );

  // Reconcile: revert failures, sync server values for successes
  const toRevert: Array<{ id: string; prevVal: boolean | undefined }> = [];
  const serverValues = new Map<string, boolean>();
  results.forEach((r, idx) => {
    const id = ids[idx];
    if (!id) return;
    if (r.status === "rejected") {
      toRevert.push({ id, prevVal: prev.get(id) });
    } else {
      serverValues.set(id, r.value.value);
    }
  });

  if (toRevert.length > 0 || serverValues.size > 0) {
    setProducts((curr) =>
      curr.map((p): ProductData => {
        if (!ids.includes(p.unifiedProductId)) return p;
        if (serverValues.has(p.unifiedProductId)) {
          return {
            ...p,
            activeForPurchase: serverValues.get(p.unifiedProductId)!,
          };
        }
        const entry = toRevert.find((x) => x.id === p.unifiedProductId);
        if (entry) {
          if (typeof entry.prevVal === "boolean") {
            return { ...p, activeForPurchase: entry.prevVal };
          }
          const clone: any = { ...p };
          delete clone.activeForPurchase;
          return clone as ProductData;
        }
        return p;
      }),
    );
    // Clear selections for any ids that ended up inactive
    setSelectedIds((prevSet) => {
      const next = new Set(prevSet);
      serverValues.forEach((val, id) => {
        if (!val) next.delete(id);
      });
      return next;
    });
  }

  closeActionsMenu();
}
